/**
 * Regulatory Rate Defaults — backward-compatibility re-export shim
 *
 * Phase 1.4.5 (Award Engine Integrity 2026-05-17) — this file is now a
 * **pure re-export shim**. The authoritative SSOT for every constant lives
 * in `packages/utils/src/award-data/`, organised by award + domain:
 *
 *   - `award-data/schads.ts`       — SCHADS Award MA000100 constants + rules
 *   - `award-data/nurses.ts`       — Nurses Award MA000034 rules + constants
 *   - `award-data/aged-care.ts`    — Aged Care Award MA000018 rules + constants
 *   - `award-data/hpss.ts`         — HPSS Award MA000027 rules + constants
 *   - `award-data/sah.ts`          — Support at Home program (Aged Care Rules 2025 Ch.4)
 *   - `award-data/qi.ts`           — AIHW Quality Indicators + QFR
 *   - `award-data/an-acc.ts`       — AN-ACC funding model
 *   - `award-data/residential.ts`  — Residential aged care fees + caps + thresholds
 *   - `award-data/compliance.ts`   — Aged Care Act civil penalties + 24/7 RN
 *   - `award-data/_shared.ts`      — Cross-cutting (CARE_MINUTE_MINIMUMS) + type re-exports
 *
 * Every constant in those files carries a provenance comment block citing
 * the primary source (FWC Pay Guide / determination / DoH publication / Act
 * + Rules reference), the effective date, the verification date + agent,
 * the verification artifact (`docs/operations/clause-verifications/...`),
 * and the next review trigger.
 *
 * This shim is retained so existing `from '../regulatory-rates'` imports
 * continue to work without per-caller updates. New code should import
 * directly from the canonical per-domain file in `award-data/`. Per-caller
 * migration to direct imports is a Phase 1.4.5b workstream and not in
 * scope for the SSOT refactor session.
 *
 * Methodology reference: `docs/operations/runbooks/award-engine-clause-provenance.md`
 * SSOT decision record: `~/.claude/projects/.../memory/project_award_engine_integrity_in_progress.md`
 * Cross-award shape-porting risk: `~/.claude/projects/.../memory/project_cross_award_shape_porting_risk.md`
 */

// ── SCHADS Award MA000100 ──
export {
  SCHADS_STANDARD_WEEKLY_RATE,
  SCHADS_STANDARD_HOURLY_RATE,
  SCHADS_RATE_SCHEDULES,
  SCHADS_CURRENT_RATE_DATE,
  schadsRateScheduleFor,
  type SchadsRateSchedule,
  SCHADS_LEVEL_RATES,
  SCHADS_SACS_ERO_RATES,
  SCHADS_HOME_CARE_AGED_RATES,
  SCHADS_HOME_CARE_DISABILITY_RATES,
  SCHADS_PERMANENT_PENALTY_RATES,
  SCHADS_CASUAL_PENALTY_RATES,
  SCHADS_CASUAL_LOADING,
  SCHADS_OVERTIME_RATES,
  SCHADS_BROKEN_SHIFT_FLAT,
  SCHADS_BROKEN_SHIFT_RATES,
  SCHADS_ALLOWANCES,
  ATO_CENTS_PER_KM_2026_27,
  SCHADS_VEHICLE_AWARD_FLOOR_2026_27,
  SCHADS_KM_FY_EXEMPT_LIMIT,
  SCHADS_LEAVE,
  SCHADS_MINIMUM_SHIFT_HOURS,
  SCHADS_ROSTER_LIMITS,
} from './award-data/schads';

// ── Support at Home (SAH) ──
export {
  SAH_ANNUAL_BUDGETS,
  SAH_BUDGET_SCHEDULES,
  sahBudgetScheduleFor,
  type SahBudgetSchedule,
  SAH_HCP_TRANSITIONED_BUDGETS,
  SAH_QUARTERLY_BUDGETS,
  type SahBudgetResolution,
  getSahBudgetForClient,
  type SahBudgetMismatch,
  type SahQuarter,
  getCurrentSahQuarter,
  getSahQuarterForDate,
  getPreviousSahQuarter,
  calculateSahCarryOver,
  depleteHcpUnspent,
  hcpUnspentTotal,
  deriveStatementHcpUnspent,
  type HcpUnspentSplit,
  type HcpUnspentDrawdownResult,
  type StatementHcpUnspent,
  splitSahBudget,
  getStoredBudgetMismatch,
  SAH_CONTRIBUTION_RATES,
  SAH_MEANS_TEST_THRESHOLDS,
  SAH_CONTRIBUTION_CAPS,
  SAH_CONTRIBUTION_CAPS_LEGACY,
  SAH_CONTRIBUTION_CAP_PERIODS,
  sahContributionCapsFor,
  type SahContributionCapPeriod,
  type SahMeansTier,
  type SahServiceCategory,
  SAH_FIXED_CONTRIBUTION_RATES,
  SAH_SLIDING_SCALE_TIERS,
  SAH_SLIDING_SCALE_RANGES,
  SAH_GRANDFATHERED_FIXED_RATES,
  SAH_GRANDFATHERED_SLIDING_RANGES,
  SAH_TIER_CONTRIBUTION_RATES,
  SAH_CARRY_OVER,
  SAH_ATHM_BUDGET_CAPS,
  SAH_ATHM_BUDGET_CAP,
  SAH_CARE_MANAGEMENT_RATE,
  SERVICE_TO_SAH_TIER,
} from './award-data/sah';

// ── AIHW Quality Indicators + QFR ──
export {
  QI_POLYPHARMACY_THRESHOLD,
  QI_SUBMISSION_DEADLINES,
  type QiQuarterKey,
  getCurrentQiSubmissionWindow,
  QFR_SUBMISSION_DEADLINES,
  QI_WEIGHT_LOSS_THRESHOLDS,
  type QIBenchmark,
  QI_BENCHMARKS,
} from './award-data/qi';

// ── Care minutes (cross-cutting) ──
export { CARE_MINUTE_MINIMUMS } from './award-data/_shared';

// ── Residential aged care fees + caps + thresholds ──
export {
  RESIDENTIAL_BASIC_DAILY_FEE,
  RESIDENTIAL_FEE_RATE_PERIODS,
  residentialFeeRatesFor,
  MAX_ACCOMMODATION_SUPPLEMENT_DAILY,
  MPIR_PCT,
  BASE_INTEREST_RATE_PCT,
  MAX_INTEREST_OUTSTANDING_ACCOM_CHARGE_PCT,
  MTCF_CAPS,
  NCCC_CAPS,
  HSC_CAPS,
  RESIDENTIAL_INCOME_THRESHOLDS,
  RESIDENTIAL_ASSET_THRESHOLDS,
  REFUNDABLE_DEPOSIT_THRESHOLDS,
  TCP_DAILY_FEES,
  NCCC_REGIME_START_DATE,
  feeRegimeForAdmission,
} from './award-data/residential';

// ── AN-ACC funding ──
export {
  AN_ACC_BASE_PRICE_DAILY,
  AN_ACC_KNOWN_BASE_PRICES,
  AN_ACC_CLASS_DAILY_RATES,
  AN_ACC_CLASS_NWAU,
  AN_ACC_RESPITE_CLASS_DAILY_RATES,
  AN_ACC_BCT_CATEGORIES,
  AN_ACC_DEFAULT_CLASSES,
  decomposeAnAccDailySubsidy,
} from './award-data/an-acc';
export type { AnAccDailySubsidyInput, AnAccDailySubsidyBreakdown } from './award-data/an-acc';

// ── Compliance — 24/7 RN + civil penalties ──
export {
  RN_24_7_REQUIREMENT_DAILY_SECONDS,
  RN_24_7_MONTHLY_REPORT_DAY,
  COMMONWEALTH_PENALTY_UNIT,
  COMMONWEALTH_PENALTY_UNIT_HISTORY,
  commonwealthPenaltyUnitFor,
  MAX_CIVIL_PENALTY_BODY_CORPORATE,
  maxCivilPenaltyBodyCorporateFor,
  MAX_CIVIL_PENALTY_INDIVIDUAL,
  maxCivilPenaltyIndividualFor,
} from './award-data/compliance';
